import { z } from 'zod';

export const black = '#000000';
export const white = '#ffffff';
export const violet = '#6666F2';
export const green = '#57E3C1';
export const orange = '#F2C67E';
export const yellow = '#E7F24E';
export const fuchsia = '#B872F2';
export const blue = '#5AAAF2';

export const ColorConfig = z.object({
  bg: z.string(),
  text: z.string(),
  border: z.string(),
  raw: z.string(),
  focusedText: z.string(),
  fill: z.string(),
  stroke: z.string(),
  selectedStroke: z.string(),
});
export type ColorConfig = z.infer<typeof ColorConfig>;

export const ColorSets = z.object({
  Primary: ColorConfig,
  Secondary: ColorConfig,
});
export type ColorSets = z.infer<typeof ColorSets>;

export const ColorConfigs = z.object({
  Aqua: ColorSets,
  Violet: ColorSets,
  Green: ColorSets,
  Orange: ColorSets,
  Yellow: ColorSets,
  Blue: ColorSets,
  Gray: ColorSets,
  LightGray: ColorSets,
  Raspberry: ColorSets,
  Lime: ColorSets,
  Royal: ColorSets,
});
export type ColorConfigs = z.infer<typeof ColorConfigs>;

export const ConfigColors: ColorConfigs = ColorConfigs.parse({
  Aqua: {
    Primary: {
      bg: 'bg-aqua-600',
      text: 'text-aqua-600',
      border: 'border-aqua-600',
      raw: '#1AAEE0',
      focusedText: 'text-white',
      stroke: 'stroke-current text-aqua-200 stroke-2 fill-transparent',
      fill: 'fill-current text-aqua-600',
      selectedStroke: 'stroke-current text-aqua-600 stroke-2 fill-transparent',
    },
    Secondary: {
      bg: 'bg-aqua-100',
      text: 'text-aqua-100',
      border: 'border-aqua-100',
      raw: '#EBFAFF',
      focusedText: 'text-white',
      stroke: 'stroke-current text-aqua-100 stroke-2 fill-transparent',
      fill: 'fill-current text-aqua-100',
      selectedStroke: 'stroke-current text-aqua-100 stroke-2 fill-transparent',
    },
  },
  Violet: {
    Primary: {
      bg: 'bg-violet-600',
      text: 'text-violet-600',
      border: 'border-violet-600',
      raw: '#933EE5',
      focusedText: 'text-white',
      stroke: 'stroke-current text-violet-200 stroke-2 fill-transparent',
      fill: 'fill-current text-violet-600',
      selectedStroke:
        'stroke-current text-violet-600 stroke-2 fill-transparent',
    },
    Secondary: {
      bg: 'bg-violet-100',
      text: 'text-violet-100',
      border: 'border-violet-100',
      raw: '#F9F5FF',
      focusedText: 'text-white',
      stroke: 'stroke-current text-violet-100 stroke-2 fill-transparent',
      fill: 'fill-current text-violet-100',
      selectedStroke:
        'stroke-current text-violet-100 stroke-2 fill-transparent',
    },
  },
  Green: {
    Primary: {
      bg: 'bg-green-600',
      text: 'text-green-600',
      border: 'border-green-600',
      raw: '#0B7F0B',
      focusedText: 'text-white',
      stroke: 'stroke-current text-green-200 stroke-2 fill-transparent',
      fill: 'fill-current text-green-600',
      selectedStroke: 'stroke-current text-green-600 stroke-2 fill-transparent',
    },
    Secondary: {
      bg: 'bg-green-100',
      text: 'text-green-100',
      border: 'border-green-100',
      raw: '#F2FAF0',
      focusedText: 'text-white',
      stroke: 'stroke-current text-green-100 stroke-2 fill-transparent',
      fill: 'fill-current text-green-100',
      selectedStroke: 'stroke-current text-green-100 stroke-2 fill-transparent',
    },
  },
  Orange: {
    Primary: {
      bg: 'bg-orange-600',
      text: 'text-orange-600',
      border: 'border-orange-600',
      raw: '#BB3D13',
      focusedText: 'text-white',
      stroke: 'stroke-current text-orange-200 stroke-2 fill-transparent',
      fill: 'fill-current text-orange-600',
      selectedStroke:
        'stroke-current text-orange-600 stroke-2 fill-transparent',
    },
    Secondary: {
      bg: 'bg-orange-100',
      text: 'text-orange-100',
      border: 'border-orange-100',
      raw: '#FAF4F2',
      focusedText: 'text-white',
      stroke: 'stroke-current text-orange-100 stroke-2 fill-transparent',
      fill: 'fill-current text-orange-100',
      selectedStroke:
        'stroke-current text-orange-100 stroke-2 fill-transparent',
    },
  },
  Yellow: {
    Primary: {
      bg: 'bg-yellow-600',
      text: 'text-yellow-600',
      border: 'border-yellow-600',
      raw: '#707005',
      focusedText: 'text-white',
      stroke: 'stroke-current text-yellow-200 stroke-2 fill-transparent',
      fill: 'fill-current text-yellow-600',
      selectedStroke:
        'stroke-current text-yellow-600 stroke-2 fill-transparent',
    },
    Secondary: {
      bg: 'bg-yellow-100',
      text: 'text-yellow-100',
      border: 'border-yellow-100',
      raw: '#F7F7E9',
      focusedText: 'text-white',
      stroke: 'stroke-current text-yellow-100 stroke-2 fill-transparent',
      fill: 'fill-current text-yellow-100',
      selectedStroke:
        'stroke-current text-yellow-100 stroke-2 fill-transparent',
    },
  },
  Blue: {
    Primary: {
      bg: 'bg-blue-600',
      text: 'text-blue-600',
      border: 'border-blue-600',
      raw: '#076DD3',
      focusedText: 'text-white',
      stroke: 'stroke-current text-blue-200 stroke-2 fill-transparent',
      fill: 'fill-current text-blue-600',
      selectedStroke: 'stroke-current text-blue-600 stroke-2 fill-transparent',
    },
    Secondary: {
      bg: 'bg-blue-100',
      text: 'text-blue-100',
      border: 'border-blue-100',
      raw: '#F0F7FF',
      focusedText: 'text-white',
      stroke: 'stroke-current text-blue-100 stroke-2 fill-transparent',
      fill: 'fill-current text-blue-100',
      selectedStroke: 'stroke-current text-blue-100 stroke-2 fill-transparent',
    },
  },
  Gray: {
    Primary: {
      bg: 'bg-gray-600',
      text: 'text-gray-600',
      border: 'border-gray-600',
      raw: '#696D70',
      focusedText: 'text-white',
      stroke: 'stroke-current text-gray-200 stroke-2 fill-transparent',
      fill: 'fill-current text-gray-600',
      selectedStroke: 'stroke-current text-gray-600 stroke-2 fill-transparent',
    },
    Secondary: {
      bg: 'bg-gray-100',
      text: 'text-gray-100',
      border: 'border-gray-100',
      raw: '#F7F7F7',
      focusedText: 'text-white',
      stroke: 'stroke-current text-gray-100 stroke-2 fill-transparent',
      fill: 'fill-current text-gray-100',
      selectedStroke: 'stroke-current text-gray-100 stroke-2 fill-transparent',
    },
  },
  LightGray: {
    Primary: {
      bg: 'bg-gray-400',
      text: 'text-gray-400',
      border: 'border-gray-400',
      raw: '#9EA1A3',
      focusedText: 'text-white',
      stroke: 'stroke-current text-gray-200 stroke-2 fill-transparent',
      fill: 'fill-current text-gray-400',
      selectedStroke: 'stroke-current text-gray-400 stroke-2 fill-transparent',
    },
    Secondary: {
      bg: 'bg-gray-300',
      text: 'text-gray-300',
      border: 'border-gray-300',
      raw: '#B8BABC',
      focusedText: 'text-white',
      stroke: 'stroke-current text-gray-100 stroke-2 fill-transparent',
      fill: 'fill-current text-gray-300',
      selectedStroke: 'stroke-current text-gray-300 stroke-2 fill-transparent',
    },
  },
  Raspberry: {
    Primary: {
      bg: 'bg-raspberry-600',
      text: 'text-raspberry-600',
      border: 'border-raspberry-600',
      raw: '#CE106F',
      focusedText: 'text-white',
      stroke: 'stroke-current text-raspberry-200 stroke-2 fill-transparent',
      fill: 'fill-current text-raspberry-600',
      selectedStroke:
        'stroke-current text-raspberry-600 stroke-2 fill-transparent',
    },
    Secondary: {
      bg: 'bg-raspberry-100',
      text: 'text-raspberry-100',
      border: 'border-raspberry-100',
      raw: '#FFF5FA',
      focusedText: 'text-white',
      stroke: 'stroke-current text-raspberry-100 stroke-2 fill-transparent',
      fill: 'fill-current text-raspberry-100',
      selectedStroke:
        'stroke-current text-raspberry-100 stroke-2 fill-transparent',
    },
  },
  Lime: {
    Primary: {
      bg: 'bg-lime-400',
      text: 'text-lime-400',
      border: 'border-lime-400',
      raw: '#59b517',
      focusedText: 'text-white',
      stroke: 'stroke-current text-lime-200 stroke-2 fill-transparent',
      fill: 'fill-current text-lime-400',
      selectedStroke: 'stroke-current text-lime-400 stroke-2 fill-transparent',
    },
    Secondary: {
      bg: 'bg-lime-100',
      text: 'text-lime-100',
      border: 'border-lime-100',
      raw: '#F0F9E9',
      focusedText: 'text-white',
      stroke: 'stroke-current text-lime-100 stroke-2 fill-transparent',
      fill: 'fill-current text-lime-100',
      selectedStroke: 'stroke-current text-lime-100 stroke-2 fill-transparent',
    },
  },
  Royal: {
    Primary: {
      bg: 'bg-royal-600',
      text: 'text-royal-600',
      border: 'border-royal-600',
      raw: '#3661e2',
      focusedText: 'text-white',
      stroke: 'stroke-current text-royal-200 stroke-2 fill-transparent',
      fill: 'fill-current text-royal-600',
      selectedStroke: 'stroke-current text-royal-600 stroke-2 fill-transparent',
    },
    Secondary: {
      bg: 'bg-royal-400',
      text: 'text-royal-400',
      border: 'border-royal-400',
      raw: '#809ff4',
      focusedText: 'text-white',
      stroke: 'stroke-current text-royal-100 stroke-2 fill-transparent',
      fill: 'fill-current text-royal-400',
      selectedStroke: 'stroke-current text-royal-400 stroke-2 fill-transparent',
    },
  },
});
